'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn, getInitials } from '@/lib/utils';
import { TennisBallIcon, getBallLevelColor } from '@/components/ui/tennis-ball-icon';
import { useSubmitEvaluation, usePlayerCriteria } from '@/hooks/use-evaluation';
import type { Player } from '@/shared/types/coach.types';
import { EVALUATION_CRITERIA as DEFAULT_CRITERIA } from '@/shared/constants/evaluation';

interface InitialEvaluation {
  attitude: number;
  effort: number;
  engagement: number;
  physical: number | null;
  technical: number | null;
  tactical: number | null;
  notes: string | null;
}

interface EvaluationModalProps {
  player: Player | null;
  sessionId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  initialEvaluation?: InitialEvaluation;
}

export function EvaluationModal({
  player,
  sessionId,
  open,
  onOpenChange,
  onSuccess,
  initialEvaluation,
}: EvaluationModalProps) {
  const [scores, setScores] = useState<Record<string, number>>({
    attitude: 0,
    effort: 0,
    engagement: 0,
    physical: 0,
    technical: 0,
    tactical: 0,
  });
  const [notes, setNotes] = useState('');

  // Seed form with existing evaluation when modal opens
  useEffect(() => {
    if (open) {
      if (initialEvaluation) {
        setScores({
          attitude: initialEvaluation.attitude,
          effort: initialEvaluation.effort,
          engagement: initialEvaluation.engagement,
          physical: initialEvaluation.physical ?? 0,
          technical: initialEvaluation.technical ?? 0,
          tactical: initialEvaluation.tactical ?? 0,
        });
        setNotes(initialEvaluation.notes ?? '');
      } else {
        setScores({ attitude: 0, effort: 0, engagement: 0, physical: 0, technical: 0, tactical: 0 });
        setNotes('');
      }
    }
  }, [open, player?.id]);
  const submitEvaluation = useSubmitEvaluation();
  const { data: customCriteria } = usePlayerCriteria(player?.id);

  // Use custom criteria if defined for this player's ball level, otherwise default
  const activeCriteria = customCriteria && customCriteria.length > 0
    ? customCriteria.map(c => ({ key: c._id, label: c.name, description: `${c.category} criteria` }))
    : DEFAULT_CRITERIA;

  const handleScoreChange = (criterion: string, rating: number) => {
    setScores(prev => ({ ...prev, [criterion]: rating }));
  };

  const handleSubmit = async () => {
    if (!player) return;

    // Validate all scores are selected
    const allSelected = Object.values(scores).every(s => s >= 1 && s <= 4);
    if (!allSelected) {
      return;
    }

    try {
      await submitEvaluation.mutateAsync({
        playerId: player.id,
        sessionId,
        attitude: scores.attitude,
        effort: scores.effort,
        engagement: scores.engagement,
        physical: scores.physical,
        technical: scores.technical,
        tactical: scores.tactical,
        notes: notes || undefined,
      });

      // Reset form
      setScores({ attitude: 0, effort: 0, engagement: 0, physical: 0, technical: 0, tactical: 0 });
      setNotes('');
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to submit evaluation:', error);
    }
  };

  const handleClose = () => {
    setScores({ attitude: 0, effort: 0, engagement: 0, physical: 0, technical: 0, tactical: 0 });
    setNotes('');
    onOpenChange(false);
  };

  const isComplete = activeCriteria.every(c => (scores[c.key] ?? 0) >= 1 && (scores[c.key] ?? 0) <= 4);
  const averageScore = isComplete
    ? (activeCriteria.reduce((sum, c) => sum + (scores[c.key] ?? 0), 0) / activeCriteria.length).toFixed(1)
    : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Quick Evaluation</DialogTitle>
        </DialogHeader>

        {player && (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Player Info */}
            <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-sm">
                {getInitials(player.firstName, player.lastName)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">
                  {player.firstName} {player.lastName}
                </div>
                <div className="text-xs text-muted-foreground capitalize flex items-center gap-1.5">
                  {player.skillLevel || 'Unassigned'}
                  {(() => {
                    const ball = getBallLevelColor(player.ballLevel);
                    return ball ? (
                      <TennisBallIcon color={ball.color} size={14} title={`${ball.label} Ball`} />
                    ) : null;
                  })()}
                </div>
              </div>
              {averageScore && (
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-primary">{averageScore}</div>
                  <div className="text-xs text-muted-foreground">Avg</div>
                </div>
              )}
            </div>

            {/* Rating Criteria - Compact 2-column grid */}
            <div className="grid grid-cols-2 gap-3">
              {activeCriteria.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-foreground">{label}</label>
                    {scores[key] > 0 && (
                      <span className="text-xs text-primary font-medium">
                        {scores[key]}/4
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map(rating => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => handleScoreChange(key, rating)}
                        className={cn(
                          'flex-1 py-2 rounded font-medium text-sm transition-all',
                          scores[key] === rating
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        )}
                      >
                        {rating}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Optional Notes */}
            <div>
              <label className="text-sm font-medium text-foreground">Notes (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional observations..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={!isComplete || submitEvaluation.isPending}
              className="w-full"
            >
              {submitEvaluation.isPending ? 'Saving...' : initialEvaluation ? 'Update Evaluation' : 'Submit Evaluation'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
